import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, X, ArrowRight, Phone, Calendar, ChevronDown, Star, Quote, Play
} from "lucide-react";
import { MarketingHeader } from "./MarketingHeader";
import { MarketingFooter } from "./MarketingFooter";

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } };
const fadeRight = { hidden: { opacity: 0, x: -40 }, visible: { opacity: 1, x: 0 } };
const fadeLeft = { hidden: { opacity: 0, x: 40 }, visible: { opacity: 1, x: 0 } };
const scaleIn = { hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1 } };

const Section = ({ children, className = "", id, testId, delay = 0 }) => (
  <motion.section
    id={id}
    data-testid={testId}
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    variants={fadeUp}
  >
    {children}
  </motion.section>
);

const StarRating = ({ count = 5 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
    ))}
  </div>
);

const MarketingLanding = ({ content, showLangSwitcher = false }) => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white font-['Manrope'] overflow-x-hidden">
      <MarketingHeader
        nav={content.nav}
        showLangSwitcher={showLangSwitcher}
        phone={content.phone}
        phoneDisplay={content.phoneDisplay}
      />

      {/* STICKY MOBILE */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-white/95 backdrop-blur-md border-t border-neutral-200 md:hidden flex gap-2" data-testid="sticky-mobile-cta">
        <Link to="/inscription" className="flex-1">
          <button className="w-full bg-[#00B8E4] text-white px-4 py-3 rounded-full font-semibold text-sm shadow-lg">
            {content.pricing.plan.ctaPrimary}
          </button>
        </Link>
        <a href={`tel:${content.phone}`} className="flex items-center justify-center w-12 h-12 rounded-full bg-[#19294C] text-white shadow-lg shrink-0">
          <Phone className="w-5 h-5" />
        </a>
      </div>

      {/* ===== HERO — like hostprotect.io ===== */}
      <section className="bg-gradient-to-b from-[#19294C] via-[#1c2f55] to-[#19294C] pt-12 pb-0 text-center" data-testid="hero-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.h1
            className="text-3xl md:text-5xl lg:text-[3.5rem] font-bold text-white font-['Outfit'] leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {content.hero.title.split(/(\{accent\}.*?\{\/accent\})/).map((part, i) => part)}
          </motion.h1>
          <motion.p
            className="mt-6 text-lg md:text-xl text-[#00B8E4] font-semibold"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {content.hero.subtitle}
          </motion.p>
          <motion.div
            className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <Link to="/inscription">
              <button className="bg-[#00B8E4] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#00a5ce] transition-all inline-flex items-center gap-2 text-base shadow-lg shadow-[#00B8E4]/25 hover:shadow-xl hover:-translate-y-0.5" data-testid="hero-cta-btn">
                {content.hero.ctaPrimary}
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <a href={`tel:${content.phone}`} className="border-2 border-white/30 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all inline-flex items-center gap-2 text-base hover:-translate-y-0.5" data-testid="hero-call-btn">
              <Phone className="w-4 h-4" />
              {content.hero.ctaCall}
            </a>
          </motion.div>
        </div>
        {/* Banner image — exactly like hostprotect.io */}
        <motion.div
          className="max-w-5xl mx-auto mt-12 px-6"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          <img
            src="/banner.jpg"
            alt="HostProtect"
            className="w-full rounded-t-2xl shadow-2xl"
          />
        </motion.div>
      </section>

      {/* ===== COMPENSATIONS ===== */}
      <Section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8FAFC]" testId="compensations-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#19294C] font-['Outfit'] text-center mb-14">
            {content.compensations.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.compensations.items.map((item, i) => (
              <motion.div
                key={i}
                className="text-center bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border border-neutral-100"
                data-testid={`compensation-card-${i}`}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <img
                  src={["/broken-screen.png", "/bedbug.png", "/cashback.png"][i]}
                  alt={item.title}
                  className="w-16 h-16 mx-auto mb-5 object-contain"
                />
                <h3 className="text-xl font-bold text-[#19294C] font-['Outfit']">{item.title}</h3>
                <p className="text-4xl font-bold text-[#00B8E4] font-['Outfit'] mt-3">{item.amount}</p>
                <p className="mt-4 text-sm text-neutral-500 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== FULL COVERAGE BANNER — dark bg + circle image ===== */}
      <section className="bg-[#19294C] py-16 md:py-20 px-6 md:px-12 overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeRight} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl md:text-4xl font-bold text-white font-['Outfit'] leading-tight">
              {content.fullCoverage.title}
            </h2>
            <Link to="/inscription" className="inline-block mt-6">
              <button className="bg-[#00B8E4] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#00a5ce] transition-all inline-flex items-center gap-2 shadow-lg hover:-translate-y-0.5">
                {content.fullCoverage.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </motion.div>
          <motion.div className="flex justify-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} transition={{ duration: 0.7 }}>
            <img src="/assu-circle.png" alt="HostProtect coverage" className="w-72 md:w-96 animate-[spin_30s_linear_infinite]" />
          </motion.div>
        </div>
      </section>

      {/* ===== GUARANTEES — 3 col with center handshake ===== */}
      <Section className="py-16 md:py-24 px-6 md:px-12 bg-white" testId="guarantees-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#19294C] font-['Outfit'] text-center mb-14">
            {content.guarantees.title}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column */}
            <div className="space-y-6">
              {content.guarantees.items.slice(0, 2).map((item, i) => (
                <motion.div key={i} className="bg-[#F8FAFC] rounded-2xl p-7 border border-neutral-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`guarantee-card-${i}`}>
                  <img src={["/bug.png", "/broken-house.png"][i]} alt={item.title} className="w-12 h-12 mb-4 object-contain" />
                  <h3 className="text-lg font-bold text-[#19294C] font-['Outfit']">{item.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
            {/* Center — CTA + handshake */}
            <motion.div
              className="bg-gradient-to-b from-[#00B8E4] to-[#2A63C5] rounded-2xl p-8 flex flex-col items-center justify-center text-center shadow-xl"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={scaleIn} transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] leading-tight">
                {content.subscribeBanner.title}
              </h3>
              <Link to="/inscription" className="mt-6">
                <button className="bg-[#19294C] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#0f1d35] transition-all inline-flex items-center gap-2 shadow-lg hover:-translate-y-0.5" data-testid="subscribe-banner-cta">
                  {content.subscribeBanner.cta}
                </button>
              </Link>
              <img src="/handshake.png" alt="" className="mt-6 w-48 md:w-56 object-contain" />
            </motion.div>
            {/* Right column */}
            <div className="space-y-6">
              {content.guarantees.items.slice(2, 4).map((item, i) => (
                <motion.div key={i} className="bg-[#F8FAFC] rounded-2xl p-7 border border-neutral-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: (i + 2) * 0.1 }}
                  data-testid={`guarantee-card-${i + 2}`}>
                  <img src={["/hotel.png", "/falling.png"][i]} alt={item.title} className="w-12 h-12 mb-4 object-contain" />
                  <h3 className="text-lg font-bold text-[#19294C] font-['Outfit']">{item.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="text-center mt-10">
            <a href={`tel:${content.phone}`}>
              <button className="bg-[#00B8E4] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#00a5ce] transition-all inline-flex items-center gap-2 shadow-lg hover:-translate-y-0.5">
                <Phone className="w-4 h-4" />
                {content.hero.ctaCall}
              </button>
            </a>
          </div>
        </div>
      </Section>

      {/* ===== USE CASES / TENSION ===== */}
      <Section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8FAFC]" testId="usecases-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#19294C] font-['Outfit'] text-center mb-4">
            {content.useCases.title}
          </h2>
          <p className="text-center text-neutral-500 mb-12">{content.useCases.subtitle}</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {content.useCases.items.map((item, i) => (
              <motion.div key={i} className="rounded-2xl overflow-hidden border border-neutral-200 bg-white shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2"
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: i * 0.15 }}
                data-testid={`usecase-card-${i}`}>
                <div className="bg-gradient-to-br from-red-50 to-orange-50 p-6 min-h-[180px]">
                  <h3 className="font-bold text-[#19294C] font-['Outfit'] text-lg mb-3">{item.scenario}</h3>
                  <p className="text-sm text-red-700/80 leading-relaxed">{item.consequence}</p>
                </div>
                <div className="p-6 border-t-4 border-[#00B8E4] bg-white">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#00B8E4] flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm text-[#19294C] leading-relaxed font-semibold">{item.solution}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== TESTIMONIALS + VIDEO ===== */}
      <Section className="py-16 md:py-24 px-6 md:px-12 bg-white" testId="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#19294C] font-['Outfit'] text-center mb-4">
            {content.video?.title || "Ils nous font confiance"}
          </h2>
          {content.video?.subtitle && <p className="text-center text-neutral-500 mb-12">{content.video.subtitle}</p>}

          {content.testimonials?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {content.testimonials.map((t, i) => (
                <motion.div key={i} className="bg-[#F8FAFC] rounded-2xl p-7 border border-neutral-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative"
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: i * 0.12 }}
                  data-testid={`testimonial-card-${i}`}>
                  <Quote className="w-10 h-10 text-[#00B8E4]/15 absolute top-5 right-5" />
                  <StarRating count={t.stars} />
                  <p className="mt-4 text-sm text-neutral-700 leading-relaxed italic">"{t.text}"</p>
                  <div className="mt-5 pt-4 border-t border-neutral-200">
                    <p className="font-bold text-[#19294C] text-sm">{t.name}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{t.role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {content.video && (
            <motion.div className="max-w-3xl mx-auto" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={scaleIn} transition={{ duration: 0.6 }}>
              {content.video.url ? (
                <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                  {(/\.(mp4|webm|ogg)(\?|$)/i.test(content.video.url) || content.video.url.startsWith("/")) ? (
                    <video
                      src={content.video.url}
                      title={content.video.title}
                      controls
                      preload="metadata"
                      playsInline
                      className="w-full h-full object-cover bg-black"
                      data-testid="testimonial-video"
                    />
                  ) : (
                    <iframe src={content.video.url} title={content.video.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  )}
                </div>
              ) : (
                <div className="aspect-video rounded-2xl bg-gradient-to-br from-[#19294C] to-[#0f1d35] flex flex-col items-center justify-center shadow-2xl relative overflow-hidden" data-testid="video-placeholder">
                  <div className="w-20 h-20 rounded-full bg-[#00B8E4]/20 flex items-center justify-center border-2 border-[#00B8E4]/40 cursor-pointer hover:bg-[#00B8E4]/30 transition-all hover:scale-110">
                    <Play className="w-8 h-8 text-[#00B8E4] ml-1" />
                  </div>
                  <p className="text-white/40 text-sm mt-4">{content.video.fallbackText}</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </Section>

      {/* ===== PRICING — side by side white/dark like hostprotect.io ===== */}
      <Section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8FAFC]" testId="pricing-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#19294C] font-['Outfit'] text-center mb-4">
            {content.pricing.title}
          </h2>
          <div className="flex justify-center mb-12">
            <p className="text-sm text-red-600 font-medium bg-red-50 px-5 py-2.5 rounded-full border border-red-200">
              {content.pricing.warning}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* LEFT — Pricing card (white) */}
            <motion.div className="bg-white rounded-2xl p-8 shadow-lg border border-neutral-100" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: 0.1 }} data-testid="pricing-card">
              <div className="text-center mb-6">
                <span className="text-5xl md:text-6xl font-bold text-[#00B8E4] font-['Outfit']">{content.pricing.plan.price}</span>
                <p className="text-sm text-neutral-500 mt-1">{content.pricing.plan.period}</p>
              </div>
              <Link to="/inscription" className="block mb-3">
                <button className="w-full bg-[#00B8E4] text-white py-4 rounded-full font-semibold hover:bg-[#00a5ce] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 text-base" data-testid="pricing-cta-btn">
                  {content.pricing.plan.ctaPrimary}
                </button>
              </Link>
              <a href={content.bookingUrl} target="_blank" rel="noopener noreferrer" className="block mb-6">
                <p className="text-center text-sm font-bold text-[#00B8E4] hover:underline cursor-pointer">
                  {content.pricing.plan.ctaVolume} <ArrowRight className="w-3 h-3 inline" />
                </p>
              </a>
              <ul className="space-y-4">
                {content.pricing.plan.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <img src="/icon-check.png" alt="" className="w-5 h-5 mt-0.5 shrink-0" />
                    <span className="text-sm text-[#19294C] font-medium">{feat.text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* RIGHT — Comparison card (dark) */}
            <motion.div className="bg-[#19294C] rounded-2xl p-8 shadow-lg text-white" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: 0.25 }}>
              <div className="flex justify-center mb-6">
                <img src="/standard.png" alt="" className="h-48 object-contain" />
              </div>
              <a href={content.bookingUrl} target="_blank" rel="noopener noreferrer" className="block mb-6">
                <button className="w-full bg-white text-[#00B8E4] py-4 rounded-full font-semibold hover:bg-neutral-100 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 text-base inline-flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" />
                  {content.pricing.plan.ctaCallback}
                </button>
              </a>
              <ul className="space-y-4">
                {content.comparison.platformDeficiencies.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <img src="/icon-x.png" alt="" className="w-5 h-5 mt-0.5 shrink-0" />
                    <span className="text-sm text-white/80">{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
          <div className="text-center mt-6 space-y-1">
            <p className="text-xs text-neutral-400">{content.pricing.plan.note}</p>
            <p className="text-xs text-neutral-400">{content.pricing.plan.priceNote}</p>
          </div>
        </div>
      </Section>

      {/* ===== VOLUME — 5+ logements ===== */}
      <section className="bg-[#19294C] py-16 md:py-20 px-6 md:px-12" data-testid="volume-section">
        <motion.div className="max-w-4xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.6 }}>
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            {content.volume.title}
          </h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">{content.volume.description}</p>
          <a href={content.bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-8">
            <button className="bg-[#00B8E4] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#00a5ce] transition-all inline-flex items-center gap-2 shadow-lg hover:-translate-y-0.5" data-testid="volume-cta">
              <Calendar className="w-4 h-4" />
              {content.volume.cta}
            </button>
          </a>
        </motion.div>
      </section>

      {/* ===== STATS ===== */}
      <Section className="py-16 md:py-24 px-6 md:px-12 bg-white" testId="stats-section">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 items-center">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-bold text-[#19294C] font-['Outfit']">{content.stats.title}</h2>
            <a href={content.bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-6">
              <button className="bg-[#00B8E4] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#00a5ce] transition-all inline-flex items-center gap-2 hover:-translate-y-0.5">
                <Phone className="w-4 h-4" />
                {content.stats.cta}
              </button>
            </a>
          </div>
          <div className="space-y-8">
            {content.stats.items.map((stat, i) => (
              <motion.div key={i} className="text-center md:text-right" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: i * 0.2 }}>
                <p className="text-5xl md:text-6xl font-bold text-[#00B8E4] font-['Outfit']">{stat.value}</p>
                <p className="mt-1 text-neutral-500 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== FAQ — image left, accordion right like hostprotect.io ===== */}
      <Section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8FAFC]" testId="faq-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left — text + image */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl md:text-3xl font-bold text-[#19294C] font-['Outfit']">{content.faq.title}</h2>
            <h3 className="text-lg text-neutral-600 mt-2 mb-4">{content.faq.subtitle}</h3>
            <p className="text-sm text-neutral-500 mb-8">
              {content.faq.bookingText}
            </p>
            <motion.img src="/faq.png" alt="FAQ" className="w-full max-w-xs mx-auto lg:mx-0 object-contain" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.6 }} />
          </div>
          {/* Right — accordion */}
          <div className="lg:col-span-3 space-y-3">
            {content.faq.items.map((item, i) => (
              <motion.div key={i} className="bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-sm"
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.4, delay: i * 0.1 }}
                data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex justify-between items-center p-5 text-left hover:bg-neutral-50 transition-colors">
                  <span className="font-bold text-[#19294C] text-sm pr-4">{item.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#00B8E4] shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                  <div className="px-5 pb-5 text-sm text-neutral-600 leading-relaxed border-t border-neutral-100 pt-4">
                    {item.answer}
                  </div>
                </div>
              </motion.div>
            ))}
            <div className="pt-4">
              <a href={`tel:${content.phone}`}>
                <button className="bg-[#00B8E4] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#00a5ce] transition-all inline-flex items-center gap-2 shadow-lg hover:-translate-y-0.5">
                  <Phone className="w-4 h-4" />
                  {content.hero.ctaCall}
                </button>
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#19294C]" data-testid="final-cta-section">
        <motion.div className="max-w-3xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.6 }}>
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">{content.finalCta.title}</h2>
          <p className="mt-4 text-white/60">{content.finalCta.subtitle}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/inscription">
              <button className="bg-[#00B8E4] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#00a5ce] transition-all inline-flex items-center gap-2 shadow-lg shadow-[#00B8E4]/25 hover:-translate-y-0.5" data-testid="final-cta-btn">
                {content.finalCta.ctaPrimary}
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <a href={content.bookingUrl} target="_blank" rel="noopener noreferrer">
              <button className="border-2 border-white/30 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all inline-flex items-center gap-2 hover:-translate-y-0.5">
                <Calendar className="w-4 h-4" />
                {content.finalCta.ctaBooking}
              </button>
            </a>
          </div>
          <a href={`tel:${content.phone}`} className="inline-flex items-center gap-2 mt-6 text-white/50 hover:text-white text-sm transition-colors">
            <Phone className="w-4 h-4" />
            {content.finalCta.ctaCall} — {content.phoneDisplay}
          </a>
        </motion.div>
      </section>

      <MarketingFooter content={content.footer} phone={content.phone} phoneDisplay={content.phoneDisplay} />
    </div>
  );
};

export default MarketingLanding;
